/**
 * ActionCard Component
 * ====================
 * Renders a proposed action with Confirm/Cancel buttons.
 */

import React, { useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { CheckCircle, XCircle, Loader2, AlertTriangle, Calendar, Plane, Bell } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { confirmAction, cancelAction } from '../../services/copilot';

export interface ActionProposal {
    type: string;
    action_id: string;
    action_type: string;
    title: string;
    description: string;
    params: Record<string, string>;
    requires_confirmation: boolean;
}

interface ActionCardProps {
    action: ActionProposal;
    onActionComplete?: (success: boolean, message: string) => void;
}

const ActionCard: React.FC<ActionCardProps> = ({ action, onActionComplete }) => {
    const { theme } = useTheme();
    const { language } = useLanguage();
    const isEs = (language as string) === 'es';
    const isDark = theme === 'dark';
    const [isLoading, setIsLoading] = useState(false);
    const [status, setStatus] = useState<'pending' | 'confirmed' | 'cancelled'>('pending');

    const getActionIcon = () => {
        switch (action.action_type) {
            case 'reserve_stand':
                return <Calendar className="w-5 h-5" />;
            case 'request_reassignment':
                return <Plane className="w-5 h-5" />;
            case 'create_alert':
                return <Bell className="w-5 h-5" />;
            default:
                return <AlertTriangle className="w-5 h-5" />;
        }
    };

    const getActionTypeLabel = () => {
        switch (action.action_type) {
            case 'reserve_stand':
                return isEs ? 'Reserva de Stand' : 'Stand Reservation';
            case 'request_reassignment':
                return isEs ? 'Reasignación de Vuelo' : 'Flight Reassignment';
            case 'create_alert':
                return isEs ? 'Crear Alerta' : 'Create Alert';
            default:
                return isEs ? 'Acción' : 'Action';
        }
    };

    const handleConfirm = async () => {
        setIsLoading(true);
        try {
            const result = await confirmAction(action.action_id);
            setStatus('confirmed');
            onActionComplete?.(result.success, result.message);
        } catch (error) {
            onActionComplete?.(false, 'Failed to confirm action');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = async () => {
        setIsLoading(true);
        try {
            const result = await cancelAction(action.action_id);
            setStatus('cancelled');
            onActionComplete?.(true, 'Action cancelled');
        } catch (error) {
            onActionComplete?.(false, 'Failed to cancel action');
        } finally {
            setIsLoading(false);
        }
    };

    const cardBg = isDark ? 'bg-gradient-to-br from-[#0a2540] to-[#001a36]' : 'bg-gradient-to-br from-orange-50 to-amber-50';
    const borderColor = isDark ? 'border-[#F26430]/30' : 'border-orange-200';
    const textPrimary = 'text-[var(--text-primary)]';
    const textSecondary = 'text-[var(--text-secondary)]';

    if (status !== 'pending') {
        return (
            <div className={`rounded-xl p-4 border ${borderColor} ${cardBg}`}>
                <div className="flex items-center gap-3">
                    {status === 'confirmed' ? (
                        <>
                            <CheckCircle className="w-5 h-5 text-green-500" />
                            <span className={`text-sm ${textPrimary}`}>{isEs ? 'Acción confirmada y ejecutada' : 'Action confirmed and executed'}</span>
                        </>
                    ) : (
                        <>
                            <XCircle className="w-5 h-5 text-[var(--text-tertiary)]" />
                            <span className={`text-sm ${textSecondary}`}>{isEs ? 'Acción cancelada' : 'Action cancelled'}</span>
                        </>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className={`rounded-xl p-4 border ${borderColor} ${cardBg} shadow-lg`}>
            {/* Header */}
            <div className="flex items-start gap-3 mb-3">
                <div className="p-2 rounded-lg bg-[#F26430]/20 text-[#F26430]">
                    {getActionIcon()}
                </div>
                <div className="flex-1">
                    <p className={`font-semibold ${textPrimary}`}>{action.title}</p>
                    <p className={`text-xs ${textSecondary}`}>{getActionTypeLabel()}</p>
                </div>
            </div>

            {/* Description */}
            <p className={`text-sm mb-3 ${textSecondary}`}>{action.description}</p>

            {/* Parameters */}
            {Object.keys(action.params).length > 0 && (
                <div className={`text-xs space-y-1 mb-4 p-2 rounded-lg ${isDark ? 'bg-[#001a36]' : 'bg-[var(--surface-raised)]0'}`}>
                    {Object.entries(action.params).filter(([_, v]) => v).map(([key, value]) => (
                        <div key={key} className="flex justify-between">
                            <span className={textSecondary}>{key.replace(/_/g, ' ')}:</span>
                            <span className={textPrimary}>{value}</span>
                        </div>
                    ))}
                </div>
            )}

            {/* Buttons */}
            <div className="flex gap-2">
                <button
                    onClick={handleConfirm}
                    disabled={isLoading}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg 
                             bg-gradient-to-r from-green-500 to-emerald-500 text-white font-medium
                             hover:from-green-600 hover:to-emerald-600 transition-all
                             disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                        <>
                            <CheckCircle className="w-4 h-4" />
                            {isEs ? 'Confirmar' : 'Confirm'}
                        </>
                    )}
                </button>
                <button
                    onClick={handleCancel}
                    disabled={isLoading}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg 
                              border font-medium transition-all
                              disabled:opacity-50 disabled:cursor-not-allowed
                              ${isDark
                            ? 'border-[var(--border)] text-gray-300 hover:bg-[var(--surface-inset)]'
                            : 'border-gray-300 text-gray-600 hover:bg-gray-100'}`}
                >
                    <XCircle className="w-4 h-4" />
                    {isEs ? 'Cancelar' : 'Cancel'}
                </button>
            </div>
        </div>
    );
};

export default ActionCard;
